// Core types for the Softlock SDK
export interface SoftlockConfig {
  /** Your Softlock project/tenant ID */
  tenantId: string;
  /** Base URL for your Softlock instance (defaults to official hosted service) */
  baseUrl?: string;
  /** API key for server-side validation (optional for client-side) */
  apiKey?: string;
  /** Cache TTL in milliseconds (default: 5 minutes) */
  cacheTtl?: number;
  /** Enable debug logging */
  debug?: boolean;
}

export interface AccessKey {
  id: string;
  key_value: string;
  status: 'active' | 'revoked' | 'expired';
  discord_user_id?: string;
  discord_tag?: string;
  created_at: string;
  expires_at?: string;
  used_at?: string;
}

export interface ValidationResult {
  valid: boolean;
  key?: AccessKey;
  error?: string;
  cached?: boolean;
}

export interface User {
  discordId?: string;
  discordTag?: string;
  userId?: string;
}

export interface UseAccessKeyOptions {
  /** Auto-validate on mount */
  autoValidate?: boolean;
  /** Refetch interval in milliseconds */
  refetchInterval?: number;
  /** Disable caching for this hook */
  disableCache?: boolean;
}

export interface UseAccessKeyResult {
  /** Validation result */
  result: ValidationResult | null;
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: string | null;
  /** Manually validate a key */
  validate: (key: string) => Promise<ValidationResult>;
  /** Clear current validation */
  clear: () => void;
  /** Retry last validation */
  retry: () => void;
}

export interface UseUserAccessOptions {
  /** Auto-refresh access data */
  autoRefresh?: boolean;
  /** Refresh interval in milliseconds */
  refreshInterval?: number;
}

export interface UseUserAccessResult {
  /** User data */
  user: User | null;
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: string | null;
  /** Manually refresh access */
  refreshAccess: () => Promise<void>;
}

export interface UseAccessGuardOptions {
  /** Fallback component for invalid access */
  fallback?: React.ReactNode;
  /** Auto-validate on mount */
  autoValidate?: boolean;
}

export interface UseAccessGuardResult {
  /** Whether access is granted */
  hasAccess: boolean;
  /** Loading state */
  loading: boolean;
  /** Error state */
  error: string | null;
  /** Validation result */
  result: ValidationResult | null;
}

export interface SoftlockMiddlewareOptions {
  /** Custom error handler */
  onError?: (error: string, req: any, res: any) => void;
  /** Custom unauthorized handler */
  onUnauthorized?: (req: any, res: any) => void;
  /** Extract key from custom location */
  extractKey?: (req: any) => string | null;
}

export interface CacheEntry {
  result: ValidationResult;
  timestamp: number;
  ttl: number;
}

// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface ValidationApiResponse extends ApiResponse {
  data?: {
    valid: boolean;
    key?: AccessKey;
    user?: User;
  };
}

// Error types
export class SoftlockError extends Error {
  constructor(
    message: string,
    public code?: string,
    public statusCode?: number
  ) {
    super(message);
    this.name = 'SoftlockError';
  }
}

export class ValidationError extends SoftlockError {
  constructor(message: string, code?: string) {
    super(message, code, 400);
    this.name = 'ValidationError';
  }
}

export class NetworkError extends SoftlockError {
  constructor(message: string, statusCode?: number) {
    super(message, 'NETWORK_ERROR', statusCode);
    this.name = 'NetworkError';
  }
}

export class ConfigurationError extends SoftlockError {
  constructor(message: string) {
    super(message, 'CONFIG_ERROR', 500);
    this.name = 'ConfigurationError';
  }
}
